import type {
  Coupon,
  CouponStatus,
  CouponType,
  CreateCoupon,
  CurrencyCode,
  DurationType,
  UpdateCoupon,
} from '@monorepo/shared'
import {
  COUPON_STATUS,
  COUPON_STATUSES,
  COUPON_TYPE,
  COUPON_TYPES,
  DURATION_TYPE,
  DURATION_TYPES,
} from '@monorepo/shared/schemas/coupons'
import { DollarSign, Loader2, Percent, RefreshCw, Save, X } from 'lucide-react'
import { useEffect, useState } from 'react'
import { Button } from '@/shared/components/ui/button'
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from '@/shared/components/ui/drawer'
import { Input } from '@/shared/components/ui/input'
import { Label } from '@/shared/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select'
import { Textarea } from '@/shared/components/ui/textarea'
import { extractErrorMessage } from '@/shared/lib/error-utils'
import { toast } from '@/shared/lib/toast'
import { useCreateCoupon, useUpdateCoupon } from '../hooks/useCoupons'

interface CouponDrawerProps {
  coupon?: Coupon
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function CouponDrawer({
  coupon,
  open,
  onOpenChange,
}: CouponDrawerProps) {
  const [formData, setFormData] = useState<
    Partial<CreateCoupon & UpdateCoupon>
  >({
    name: '',
    code: '',
    description: '',
    coupon_type: COUPON_TYPE.percentage,
    percentage_rate: 10,
    amount_cents: undefined,
    currency: undefined,
    duration_type: DURATION_TYPE.forever,
    duration_in_months: undefined,
    redeem_after: undefined,
    redeem_before: undefined,
    max_redemptions: undefined,
    status: COUPON_STATUS.draft,
    metadata: {},
  })

  const updateCoupon = useUpdateCoupon()
  const createCoupon = useCreateCoupon()

  // Update form data when coupon changes or reset when creating
  useEffect(() => {
    if (coupon) {
      // Edit mode: populate with coupon data
      setFormData({
        name: coupon.name,
        code: coupon.code,
        description: coupon.description || '',
        coupon_type: coupon.coupon_type,
        percentage_rate: coupon.percentage_rate || undefined,
        amount_cents: coupon.amount_cents || undefined,
        currency: coupon.currency || undefined,
        duration_type: coupon.duration_type,
        duration_in_months: coupon.duration_in_months || undefined,
        redeem_after: coupon.redeem_after || undefined,
        redeem_before: coupon.redeem_before || undefined,
        max_redemptions: coupon.max_redemptions || undefined,
        status: coupon.status,
        metadata: coupon.metadata || {},
      })
    } else {
      // Create mode: reset to empty form
      setFormData({
        name: '',
        code: '',
        description: '',
        coupon_type: COUPON_TYPE.percentage,
        percentage_rate: 10,
        amount_cents: undefined,
        currency: undefined,
        duration_type: DURATION_TYPE.forever,
        duration_in_months: undefined,
        redeem_after: undefined,
        redeem_before: undefined,
        max_redemptions: undefined,
        status: COUPON_STATUS.draft,
        metadata: {},
      })
    }
  }, [coupon])

  const generateCouponCode = () => {
    // Extract prefix from name (first 6-8 chars, uppercase, alphanumeric only)
    const prefix =
      formData.name
        ?.slice(0, 6)
        .toUpperCase()
        .replace(/[^A-Z0-9]/g, '') || 'COUPON'

    // Generate random suffix (4 characters)
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789'
    let suffix = ''
    for (let i = 0; i < 4; i++) {
      suffix += chars.charAt(Math.floor(Math.random() * chars.length))
    }

    return `${prefix}${suffix}`
  }

  const handleGenerateCode = () => {
    setFormData(prev => ({ ...prev, code: generateCouponCode() }))
  }

  const handleSave = async () => {
    try {
      // Prepare data based on coupon_type
      const baseData = {
        name: formData.name!,
        code: formData.code!.toUpperCase(),
        description: formData.description || undefined,
        coupon_type: formData.coupon_type!,
        duration_type: formData.duration_type!,
        duration_in_months:
          formData.duration_type === DURATION_TYPE.months
            ? formData.duration_in_months
            : undefined,
        redeem_after: formData.redeem_after || undefined,
        redeem_before: formData.redeem_before || undefined,
        max_redemptions: formData.max_redemptions || undefined,
        status: formData.status!,
        metadata: formData.metadata || {},
      }

      // Add type-specific fields
      const preparedData: CreateCoupon =
        formData.coupon_type === COUPON_TYPE.percentage
          ? {
              ...baseData,
              percentage_rate: formData.percentage_rate!,
              amount_cents: null,
              currency: null,
            }
          : {
              ...baseData,
              amount_cents: formData.amount_cents!,
              currency: formData.currency!,
              percentage_rate: null,
            }

      if (coupon) {
        // Edit mode: update existing coupon
        await updateCoupon.mutateAsync({
          id: coupon.id,
          data: preparedData,
        })
        onOpenChange(false)
      } else {
        // Create mode: create new coupon
        await createCoupon.mutateAsync(preparedData)
        onOpenChange(false)
      }
    } catch (error) {
      toast.error(extractErrorMessage(error))
    }
  }

  const handleCancel = () => {
    onOpenChange(false)
  }

  const hasChanges =
    coupon &&
    (formData.name !== coupon.name ||
      formData.code !== coupon.code ||
      formData.description !== (coupon.description || '') ||
      formData.coupon_type !== coupon.coupon_type ||
      formData.percentage_rate !== coupon.percentage_rate ||
      formData.amount_cents !== coupon.amount_cents ||
      formData.currency !== coupon.currency ||
      formData.duration_type !== coupon.duration_type ||
      formData.duration_in_months !== coupon.duration_in_months ||
      formData.status !== coupon.status)

  const isValid = () => {
    if (!formData.name?.trim() || !formData.code?.trim()) return false
    if (
      formData.coupon_type === COUPON_TYPE.percentage &&
      !formData.percentage_rate
    )
      return false
    if (
      formData.coupon_type === COUPON_TYPE.fixed_amount &&
      (!formData.amount_cents || !formData.currency)
    )
      return false
    if (
      formData.duration_type === DURATION_TYPE.months &&
      !formData.duration_in_months
    )
      return false
    return true
  }

  return (
    <Drawer open={open} onOpenChange={onOpenChange} direction='right'>
      <DrawerContent className='h-full w-[50vw] min-w-[600px] max-w-[800px] ml-auto fixed right-0'>
        <DrawerHeader>
          <DrawerTitle>
            {coupon ? 'Edit Coupon' : 'Create New Coupon'}
          </DrawerTitle>
          <DrawerDescription>
            {coupon
              ? 'Update the coupon details and discount settings.'
              : 'Create a new promotional coupon for your customers.'}
          </DrawerDescription>
        </DrawerHeader>

        <div className='flex-1 overflow-y-auto px-4'>
          <div className='space-y-4'>
            {/* Coupon Name */}
            <div>
              <Label htmlFor='coupon-name'>Coupon Name *</Label>
              <Input
                id='coupon-name'
                value={formData.name}
                onChange={e =>
                  setFormData(prev => ({ ...prev, name: e.target.value }))
                }
                placeholder='e.g., Summer Sale 2024'
                className='mt-1'
              />
            </div>

            {/* Coupon Code */}
            <div>
              <Label htmlFor='coupon-code'>Coupon Code *</Label>
              <div className='flex items-center gap-2 mt-1'>
                <Input
                  id='coupon-code'
                  value={formData.code ?? ''}
                  onChange={e =>
                    setFormData(prev => ({
                      ...prev,
                      code: e.target.value.toUpperCase(),
                    }))
                  }
                  placeholder='e.g., SUMMER20'
                  className='font-mono'
                  disabled={!!coupon} // Can't change code in edit mode
                />
                {!coupon && (
                  <Button
                    type='button'
                    variant='outline'
                    size='sm'
                    onClick={handleGenerateCode}
                    className='px-3'
                    title='Generate code from name'
                  >
                    <RefreshCw className='w-4 h-4' />
                  </Button>
                )}
              </div>
              <p className='text-xs text-slate-500 mt-1'>
                {coupon
                  ? 'Code cannot be changed after creation'
                  : 'Click generate button to create a code from the name'}
              </p>
            </div>

            {/* Description */}
            <div>
              <Label htmlFor='description'>Description</Label>
              <Textarea
                id='description'
                value={formData.description || ''}
                onChange={e =>
                  setFormData(prev => ({
                    ...prev,
                    description: e.target.value,
                  }))
                }
                placeholder="Describe this coupon's offer"
                rows={3}
                className='mt-1'
              />
            </div>

            {/* Coupon Type */}
            <div>
              <Label htmlFor='coupon-type'>Discount Type *</Label>
              <Select
                value={formData.coupon_type}
                onValueChange={(value: CouponType) => {
                  setFormData(prev => ({
                    ...prev,
                    coupon_type: value,
                    // Reset type-specific fields
                    percentage_rate:
                      value === COUPON_TYPE.percentage ? 10 : undefined,
                    amount_cents:
                      value === COUPON_TYPE.fixed_amount ? 1000 : undefined,
                    currency:
                      value === COUPON_TYPE.fixed_amount ? 'USD' : undefined,
                  }))
                }}
              >
                <SelectTrigger className='mt-1'>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {COUPON_TYPES.map(type => (
                    <SelectItem key={type} value={type}>
                      <div className='flex items-center gap-2'>
                        {type === COUPON_TYPE.percentage ? (
                          <Percent className='w-4 h-4' />
                        ) : (
                          <DollarSign className='w-4 h-4' />
                        )}
                        <span className='capitalize'>
                          {type.replace('_', ' ')}
                        </span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Conditional: Percentage Rate */}
            {formData.coupon_type === COUPON_TYPE.percentage && (
              <div>
                <Label htmlFor='percentage-rate'>Discount Percentage *</Label>
                <Input
                  id='percentage-rate'
                  type='number'
                  min='0'
                  max='100'
                  step='0.01'
                  value={formData.percentage_rate || ''}
                  onChange={e =>
                    setFormData(prev => ({
                      ...prev,
                      percentage_rate: parseFloat(e.target.value),
                    }))
                  }
                  placeholder='20'
                  className='mt-1'
                />
                <p className='text-xs text-slate-500 mt-1'>
                  Percentage off the original price (0-100)
                </p>
              </div>
            )}

            {/* Conditional: Fixed Amount Fields */}
            {formData.coupon_type === COUPON_TYPE.fixed_amount && (
              <div className='grid grid-cols-1 sm:grid-cols-2 gap-4'>
                <div>
                  <Label htmlFor='currency'>Currency *</Label>
                  <Select
                    value={formData.currency || ''}
                    onValueChange={(value: CurrencyCode) =>
                      setFormData(prev => ({ ...prev, currency: value }))
                    }
                  >
                    <SelectTrigger className='mt-1'>
                      <SelectValue placeholder='Select currency' />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value='USD'>USD</SelectItem>
                      <SelectItem value='EUR'>EUR</SelectItem>
                      <SelectItem value='GBP'>GBP</SelectItem>
                      <SelectItem value='CAD'>CAD</SelectItem>
                      <SelectItem value='AUD'>AUD</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor='amount'>Discount Amount *</Label>
                  <Input
                    id='amount'
                    type='number'
                    min='0'
                    step='0.01'
                    value={
                      formData.amount_cents
                        ? (formData.amount_cents / 100).toFixed(2)
                        : ''
                    }
                    onChange={e =>
                      setFormData(prev => ({
                        ...prev,
                        amount_cents: Math.round(
                          parseFloat(e.target.value) * 100
                        ),
                      }))
                    }
                    placeholder='10.00'
                    className='mt-1'
                  />
                </div>
                <p className='text-xs text-slate-500 col-span-1 sm:col-span-2 -mt-2'>
                  Fixed amount to discount from the total
                </p>
              </div>
            )}

            {/* Duration Type */}
            <div>
              <Label htmlFor='duration-type'>Duration *</Label>
              <Select
                value={formData.duration_type}
                onValueChange={(value: DurationType) => {
                  setFormData(prev => ({
                    ...prev,
                    duration_type: value,
                    duration_in_months:
                      value === DURATION_TYPE.months ? 1 : undefined,
                  }))
                }}
              >
                <SelectTrigger className='mt-1'>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {DURATION_TYPES.map(type => (
                    <SelectItem key={type} value={type}>
                      <span className='capitalize'>{type}</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Conditional: Duration in Months */}
            {formData.duration_type === DURATION_TYPE.months && (
              <div>
                <Label htmlFor='duration-months'>Duration in Months *</Label>
                <Input
                  id='duration-months'
                  type='number'
                  min='1'
                  max='120'
                  value={formData.duration_in_months || ''}
                  onChange={e =>
                    setFormData(prev => ({
                      ...prev,
                      duration_in_months: parseInt(e.target.value),
                    }))
                  }
                  placeholder='6'
                  className='mt-1'
                />
                <p className='text-xs text-slate-500 mt-1'>
                  Number of months the discount applies
                </p>
              </div>
            )}

            {/* Max Redemptions */}
            <div>
              <Label htmlFor='max-redemptions'>Max Redemptions</Label>
              <Input
                id='max-redemptions'
                type='number'
                min='1'
                value={formData.max_redemptions || ''}
                onChange={e =>
                  setFormData(prev => ({
                    ...prev,
                    max_redemptions: e.target.value
                      ? parseInt(e.target.value)
                      : undefined,
                  }))
                }
                placeholder='Unlimited'
                className='mt-1'
              />
              <p className='text-xs text-slate-500 mt-1'>
                Leave blank for unlimited redemptions
              </p>
            </div>

            {/* Status */}
            <div>
              <Label htmlFor='status'>Status *</Label>
              <Select
                value={formData.status}
                onValueChange={(value: CouponStatus) =>
                  setFormData(prev => ({ ...prev, status: value }))
                }
              >
                <SelectTrigger className='mt-1'>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {COUPON_STATUSES.map(status => (
                    <SelectItem key={status} value={status}>
                      <span className='capitalize'>{status}</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className='text-xs text-slate-500 mt-1'>
                Active coupons can be redeemed, draft coupons are hidden
              </p>
            </div>

            {/* Coupon Info */}
            {coupon && (
              <div className='pt-4 border-t'>
                <h4 className='font-medium text-sm text-slate-700 dark:text-slate-300 mb-3'>
                  Coupon Information
                </h4>
                <div className='space-y-2 text-xs text-slate-500'>
                  <div>
                    <span className='font-medium'>ID:</span> {coupon.id}
                  </div>
                  <div>
                    <span className='font-medium'>Created:</span>{' '}
                    {new Date(coupon.created_at).toLocaleDateString()}
                  </div>
                  <div>
                    <span className='font-medium'>Updated:</span>{' '}
                    {new Date(coupon.updated_at).toLocaleDateString()}
                  </div>
                  <div>
                    <span className='font-medium'>Redemptions:</span>{' '}
                    {coupon.times_redeemed}{' '}
                    {coupon.max_redemptions
                      ? `/ ${coupon.max_redemptions}`
                      : '/ ∞'}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        <DrawerFooter>
          <div className='flex gap-2 w-full'>
            <DrawerClose asChild>
              <Button
                variant='outline'
                onClick={handleCancel}
                disabled={updateCoupon.isPending || createCoupon.isPending}
                className='flex-1'
              >
                <X className='w-4 h-4 mr-2' />
                Cancel
              </Button>
            </DrawerClose>
            <Button
              onClick={handleSave}
              disabled={
                updateCoupon.isPending ||
                createCoupon.isPending ||
                (coupon && !hasChanges) ||
                !isValid()
              }
              className='flex-1'
            >
              {(updateCoupon.isPending || createCoupon.isPending) && (
                <Loader2 className='w-4 h-4 mr-2 animate-spin' />
              )}
              <Save className='w-4 h-4 mr-2' />
              {coupon ? 'Save Changes' : 'Create Coupon'}
            </Button>
          </div>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  )
}
